import { useState, useEffect } from 'react'
import DataTable from './DataTable'
import { KTSVG } from '../../_metronic/helpers'
import Swal from 'sweetalert2'
import Select from 'react-select'
import { Modal, Button, Form, Table } from 'react-bootstrap'
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper'

type SelectOption = { label: string; value: number | string }

function SimilarItemList() {
    const hasPermission = 1
    const [newData, setNewData] = useState({})
    const [availableItems, setAvailableItems] = useState<SelectOption[]>([])
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [showDetailsModal, setShowDetailsModal] = useState(false)
    const [detailsData, setDetailsData] = useState<{ group_name: string; items: any[] } | null>(null)
    const [editingGroupId, setEditingGroupId] = useState<number | null>(null)
    const [createForm, setCreateForm] = useState({
        group_name: '',
        selectedItems: [] as SelectOption[],
    })
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoadingDetails, setIsLoadingDetails] = useState(false)
    const [isLoadingForm, setIsLoadingForm] = useState(false)

    useEffect(() => {
        if (showCreateModal) {
            getAvailableItems(editingGroupId ?? undefined)
        }
    }, [showCreateModal, editingGroupId])

    const getAvailableItems = (excludeGroupId?: number) => {
        const url = excludeGroupId
            ? `similar-item-groups/available-items?exclude_group_id=${excludeGroupId}`
            : 'similar-item-groups/available-items'
        fetch(process.env.REACT_APP_API_URL + url, getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const list = (resp.data || resp).map((item: any) => ({
                    label: `${item.categoryName || item.category_name || 'N/A'} - ${item.itemName || item.item_name}`,
                    value: item.idItems ?? item.id_items,
                }))
                setAvailableItems(list)
            })
            .catch((error) => console.log(error, 'getAvailableItems'))
    }

    const handleOpenCreateModal = () => {
        setEditingGroupId(null)
        setCreateForm({ group_name: '', selectedItems: [] })
        setShowCreateModal(true)
    }

    const handleOpenEditModal = (id: number) => {
        setEditingGroupId(id)
        setCreateForm({ group_name: '', selectedItems: [] })
        setShowCreateModal(true)
        setIsLoadingForm(true)
        fetch(process.env.REACT_APP_API_URL + `similar-item-groups/${id}`, getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                if (resp.success && resp.data) {
                    const items = (resp.data.items || []).map((item: any) => ({
                        label: `${item.category_name || 'N/A'} - ${item.item_name || ''}`,
                        value: item.id_items,
                    }))
                    setCreateForm({
                        group_name: resp.data.group_name || '',
                        selectedItems: items,
                    })
                }
            })
            .catch((error) => console.log(error))
            .finally(() => setIsLoadingForm(false))
    }

    const handleCloseCreateModal = () => {
        setShowCreateModal(false)
        setEditingGroupId(null)
        setCreateForm({ group_name: '', selectedItems: [] })
    }

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        const trimmedName = (createForm.group_name || '').trim()
        if (!trimmedName) {
            Swal.fire({
                icon: 'warning',
                title: 'Validation Error',
                text: 'Group name is required',
            })
            return
        }
        if (!createForm.selectedItems || createForm.selectedItems.length < 2) {
            Swal.fire({
                icon: 'warning',
                title: 'Validation Error',
                text: 'Please select at least 2 items for the group',
            })
            return
        }
        setIsSubmitting(true)
        const payload = {
            group_name: trimmedName,
            id_items: createForm.selectedItems.map((o) => o.value),
        }
        const isEdit = editingGroupId !== null
        const url = isEdit
            ? `similar-item-groups/update/${editingGroupId}`
            : 'similar-item-groups/store'
        fetch(process.env.REACT_APP_API_URL + url, postRequestOptions(payload))
            .then((resp) => resp.json())
            .then((resp) => {
                if (resp.success) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Success!',
                        text: resp.successMessage || (isEdit ? 'Group updated successfully' : 'Group created successfully'),
                    })
                    handleCloseCreateModal()
                    setNewData({ id: Date.now() })
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: resp.message || (isEdit ? 'Failed to update group' : 'Failed to create group'),
                    })
                }
            })
            .catch((error) => {
                console.log(error)
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'An error occurred',
                })
            })
            .finally(() => setIsSubmitting(false))
    }

    const handleDetailsClick = (id: number) => {
        setIsLoadingDetails(true)
        setShowDetailsModal(true)
        setDetailsData(null)
        fetch(process.env.REACT_APP_API_URL + `similar-item-groups/${id}`, getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                if (resp.success && resp.data) {
                    setDetailsData({
                        group_name: resp.data.group_name || 'N/A',
                        items: resp.data.items || [],
                    })
                }
            })
            .catch((error) => console.log(error))
            .finally(() => setIsLoadingDetails(false))
    }

    const handleCloseDetailsModal = () => {
        setShowDetailsModal(false)
        setDetailsData(null)
    }

    const deleteSimilarItemGroup = (id: number): void => {
        if (window.confirm('Are you sure you want to delete this similar item group?')) {
            const myToken: string | null = localStorage.getItem('MyToken')
            const jwt: string | null = myToken != null ? JSON.parse(myToken) : null
            fetch(`${process.env.REACT_APP_API_URL}similar-item-groups/${id}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
            })
                .then((resp: Response) => resp.json())
                .then((resp: any) => {
                    if (resp.success) {
                        Swal.fire({
                            icon: 'success',
                            title: 'Deleted!',
                            text: resp.successMessage || 'Group deleted successfully',
                        })
                        setNewData({ id: Date.now() })
                    }
                })
                .catch((error: any) => console.log(error))
        }
    }

    const columns = [
        { value: 'id_similar_item_groups', title: 'Id', style: { textAlign: 'center' } },
        { value: 'group_name', title: 'Group Name', style: { textAlign: 'left' } },
        { value: 'items_count', title: 'Items Count', style: { textAlign: 'center' } },
        {
            value: 'action',
            title: 'Action',
            style: { textAlign: 'left', whiteSpace: 'nowrap' },
            hasActionsComponent: true,
            actionsComponent: (row: any) => (
                <>
                    <button
                        className="btn btn-light-primary btn-sm p-2 me-1"
                        onClick={() => handleDetailsClick(row.id_similar_item_groups)}
                    >
                        <KTSVG path="/media/svg/icons/General/Visible.svg" className="svg-icon-5 svg-icon" />
                        Details
                    </button>
                    <button
                        className="btn btn-light-info btn-sm p-2 me-1"
                        onClick={() => handleOpenEditModal(row.id_similar_item_groups)}
                    >
                        <KTSVG path="/media/svg/icons/Communication/Write.svg" className="svg-icon-5 svg-icon" />
                        Edit
                    </button>
                    <button
                        className="btn btn-light-danger btn-sm p-2"
                        onClick={() => deleteSimilarItemGroup(row.id_similar_item_groups)}
                    >
                        <KTSVG path="/media/svg/icons/General/Trash.svg" className="svg-icon-5 svg-icon" />
                        Delete
                    </button>
                </>
            ),
        },
    ]

    const filters = {
        id_similar_item_groups: { filterType: 'like', filterValue: '' },
        group_name: { filterType: 'like', filterValue: '' },
        items_count: { filterType: 'like', filterValue: '' },
    }

    const url = process.env.REACT_APP_API_URL + 'similar-item-groups'

    return (
        <>
            {hasPermission === 1 && (
                <div className="card card-custom">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <div className="card-title">
                            <KTSVG
                                path="/media/svg/icons/Text/Bullet-list.svg"
                                className="svg-icon-1 svg-icon-primary"
                            />
                            <span className="text fw-bolder" style={{ marginLeft: '10px' }}>
                                Similar Items List
                            </span>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={handleOpenCreateModal}
                        >
                            <i className="fa fa-plus"></i> New Similar Item Group
                        </button>
                    </div>

                    <div className="card-body">
                        <DataTable url={url} columns={columns} filters={filters} newData={newData} />
                    </div>
                </div>
            )}

            {/* Create / Edit Group Modal */}
            <Modal show={showCreateModal} onHide={handleCloseCreateModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingGroupId ? 'Edit Similar Item Group' : 'New Similar Item Group'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleFormSubmit}>
                    <Modal.Body>
                        {isLoadingForm ? (
                            <div className="text-center py-4">Loading...</div>
                        ) : (
                            <>
                        <Form.Group className="mb-3">
                            <Form.Label>Group Name *</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter group name"
                                value={createForm.group_name}
                                onChange={(e) =>
                                    setCreateForm((prev) => ({ ...prev, group_name: e.target.value }))
                                }
                                maxLength={500}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Select Similar Items (at least 2) *</Form.Label>
                            <Select
                                isMulti
                                value={createForm.selectedItems}
                                onChange={(options) =>
                                    setCreateForm((prev) => ({
                                        ...prev,
                                        selectedItems: (options as SelectOption[]) || [],
                                    }))
                                }
                                options={availableItems}
                                placeholder="Select items..."
                                isClearable
                            />
                            <Form.Text className="text-muted">
                                {editingGroupId
                                    ? 'Items in other groups are excluded. Each item can belong to only one group.'
                                    : 'Only items not already in a group are shown. Each item can belong to only one group.'}
                            </Form.Text>
                        </Form.Group>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseCreateModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={isSubmitting || isLoadingForm}>
                            {isSubmitting ? 'Saving...' : editingGroupId ? 'Update' : 'Save'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Details Modal */}
            <Modal show={showDetailsModal} onHide={handleCloseDetailsModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Group Details {detailsData?.group_name && `: ${detailsData.group_name}`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isLoadingDetails ? (
                        <div className="text-center py-4">Loading...</div>
                    ) : detailsData ? (
                        detailsData.items.length > 0 ? (
                            <Table striped bordered hover size="sm">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Item Name</th>
                                        <th>Item Code</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detailsData.items.map((item: any, idx: number) => (
                                        <tr key={item.id_similar_items}>
                                            <td>{idx + 1}</td>
                                            <td>{item.item_name || '-'}</td>
                                            <td>{item.item_code || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <p className="text-muted mb-0">No items in this group.</p>
                        )
                    ) : (
                        <p className="text-muted mb-0">No data to display.</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseDetailsModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default SimilarItemList
